import {useEffect, useState} from "react";
import {useUserTranslationsMutation} from "./useUserTranslationsMutation";

export interface UseUserTranslationsViewArgs {
    wordId: string;
    translations: string[];
    userTranslations?: string[];
}

export const useUserTranslationsView = ({wordId, translations, userTranslations = []}: UseUserTranslationsViewArgs) => {

    const [isEditingUserTranslations, setIsEditingUserTranslations] = useState<boolean>(false);
    const [currentUserTranslations, setCurrentUserTranslations] = useState<string[]>(userTranslations);
    const [editedUserTranslations, setEditedUserTranslations] = useState<string[]>(userTranslations);
    const [currentlyEditedUserTranslation, setCurrentlyEditedUserTranslation] = useState<string>("");

    const {mutateAsync: saveUserTranslations, isPending: isSavingUserTranslations} = useUserTranslationsMutation({wordId});

    useEffect(() => {
        setCurrentUserTranslations(userTranslations);
        setEditedUserTranslations(userTranslations);
    }, [userTranslations]);

    const startEditing = () => {
        setIsEditingUserTranslations(true);
        setCurrentlyEditedUserTranslation("");
    };

    const cancelEditing = () => {
        setEditedUserTranslations(currentUserTranslations);
        setCurrentlyEditedUserTranslation("");
        setIsEditingUserTranslations(false);
    };

    const normalizeTranslation = (translation: string) => translation.trim();

    const uniqueTranslations = (translations: string[]) => {
        const seen = new Set<string>();
        return translations.reduce<string[]>((result, translation) => {
            const normalized = normalizeTranslation(translation);
            if (!normalized || seen.has(normalized.toLowerCase())) {
                return result;
            }

            seen.add(normalized.toLowerCase());
            result.push(normalized);
            return result;
        }, []);
    };

    const saveEditing = async () => {
        const trimmed = normalizeTranslation(currentlyEditedUserTranslation);
        const nextEditedUserTranslations = trimmed
            ? uniqueTranslations([...editedUserTranslations, trimmed])
            : editedUserTranslations;

        setEditedUserTranslations(nextEditedUserTranslations);
        setCurrentUserTranslations(nextEditedUserTranslations);
        setCurrentlyEditedUserTranslation("");

        await saveUserTranslations(nextEditedUserTranslations);
        setIsEditingUserTranslations(false);
    };

    const addEditedTranslation = () => {
        const trimmed = normalizeTranslation(currentlyEditedUserTranslation);
        if (!trimmed) {
            return;
        }

        setEditedUserTranslations(prev => uniqueTranslations([...prev, trimmed]));
        setCurrentlyEditedUserTranslation("");
    };

    const removeEditedTranslation = (index: number) => {
        setEditedUserTranslations(prev => prev.filter((_, i) => i !== index));
    };

    const handleInputKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            if (currentlyEditedUserTranslation.trim() !== "") {
                addEditedTranslation();
                return;
            }

            event.preventDefault();
            await saveEditing();
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            cancelEditing();
        }
    };

    return {
        isEditingUserTranslations,
        currentUserTranslations,
        editedUserTranslations,
        currentlyEditedUserTranslation,
        isSavingUserTranslations,
        startEditing,
        cancelEditing,
        saveEditing,
        addEditedTranslation,
        removeEditedTranslation,
        setCurrentlyEditedUserTranslation,
        handleInputKeyDown,
    };
};
